/**
 * Team tab: who is at the table, the Lead's tools, my secret objective,
 * and a plain reminder of what my seat does.
 */
import type { Affordance, Role, SeatCommand, SeatView } from '@nightshift/engine/client';
import type { ReactElement } from 'react';
import { Fold, RESOURCE_TITLE, ROLE_TITLE, Secret } from '../ui/bits.tsx';
import { forecastText } from './BoardTab.tsx';

const SEAT_JOB: Record<Role, string> = {
  EM: 'New patients arrive in your bays. Assess, stabilise Reds, and refer on. Your bays fill first, so keep them moving.',
  SURG: 'You run the surgical ward. Operations in the first Theatre need anaesthetic cover from the Intensivist. A booked second Theatre includes its own anaesthetist.',
  PHYS: 'You run the medical ward and give physician opinions. Many patients need you for a medical route or a second look.',
  PSYCH: 'You run the Quiet Wing and give psychiatry opinions. Some patients only get better with you.',
  ICU: 'You run ICU, give the anaesthetic for the first Theatre, and Support each ICU patient every turn. Step them down when the receiving ward accepts them.',
};

type TeamControls = { v: SeatView; respond: (cmd: SeatCommand) => Promise<boolean>; busy: boolean };

/** Keep proposed referrals separate from transfers already reserved for release. */
function Referrals({ v, respond, busy }: TeamControls): ReactElement {
  return <section className="lead-tools" aria-label="Referrals">
    <h2 className="sec-title">Referrals</h2>
    {v.coordination.referrals.length === 0 && v.coordination.pendingTransfers.length === 0 && <p className="muted">No referrals.</p>}
    {v.coordination.referrals.map(offer => <ReferralOffer key={`${offer.sender}:${offer.actionId}`} v={v} respond={respond} busy={busy} offer={offer} />)}
    {v.coordination.pendingTransfers.map(move => <p key={move.patient.id} data-testid={`pending-transfer-${move.patient.id}`}>
      <strong>{move.patient.name}</strong>: {ROLE_TITLE[move.from]} → {ROLE_TITLE[move.to]}. Accepted. Arrives at the start of turn {move.arrivesTurn}; the receiving team can act then.
    </p>)}
  </section>;
}

/** Bind explicit capacity permission to the current offer and receiving draft. */
function ReferralOffer({ v, respond, busy, offer }: TeamControls & { offer: SeatView['coordination']['referrals'][number] }): ReactElement {
  const incoming = offer.receiver === v.me.role;
  const disabled = busy || !v.draft || v.clock.phase !== 'ACT' || v.clock.paused;
  const accept = (mode: 'BED' | 'OVERFLOW' | null) => respond({ type: 'RESPOND_REFERRAL', turn: v.clock.turn, expectedRevision: v.draft!.revision, sender: offer.sender, actionId: offer.actionId, offerRevision: offer.offerRevision, mode });
  return <article className="kit" data-testid={`referral-${offer.sender}-${offer.actionId}`}>
    <h3>{offer.patient.name} · {ROLE_TITLE[offer.sender]} → {ROLE_TITLE[offer.receiver]}</h3>
    <p>{offer.patient.colour} · {offer.patient.believed} · {offer.patient.box}% sure{offer.patient.route ? ` · ${offer.patient.route} route` : ''}</p>
    <strong>{offer.mode === 'BED' ? 'Accepted: bed only' : offer.mode === 'OVERFLOW' ? 'Accepted: even without a free bed' : 'Waiting for an answer'}</strong>
    <p className="muted">If the receiving team has not agreed, or has no room, when the turn ends, the patient stays put. Accepted patients arrive at the start of the next turn.</p>
    {incoming && <>
      <p>Bed only: you take the patient only if a bed is free once others have left. Even without a bed: the patient comes anyway, which adds to next turn’s workload and may delay one discharge. ICU always needs a free bed and the right equipment.</p>
      <div className="row wrap">
        <button className="btn btn-small" disabled={disabled || offer.mode === 'BED'} aria-pressed={offer.mode === 'BED'} onClick={() => accept('BED')}>Bed only</button>
        {v.me.role !== 'ICU' && <button className="btn btn-small" disabled={disabled || offer.mode === 'OVERFLOW'} aria-pressed={offer.mode === 'OVERFLOW'} onClick={() => accept('OVERFLOW')}>Even without a bed</button>}
        <button className="btn btn-small" disabled={disabled || !offer.mode} onClick={() => accept(null)}>Withdraw acceptance</button>
      </div>
    </>}
  </article>;
}

/** Show the roster, role reminder and secret objective. */
function TeamDetails({ v }: { v: SeatView }): ReactElement {
  return <>
    <section aria-label="Your table">
      <h3 className="sec-title">Table {v.me.table}</h3>
      <ul className="mates">{v.board.seats.map(s => <li key={s.role} className={`spec-${s.role} ${s.role === v.me.role ? 'me' : ''}`}>
        <span className="spec-dot" /><span className="mate-name">{ROLE_TITLE[s.role]}<span className="muted"> {s.bot ? '(bot)' : s.name}</span></span>
        {v.board.lead === s.role && <span className="lead-badge">Lead</span>}
        {s.beds !== null && <span className="mate-beds muted">{v.beds.filter(x => x.patient).length}/{s.beds}</span>}
      </li>)}</ul>
      <p className="muted">You cannot see each other’s lists unless a card is shared.</p>
    </section>
    <Fold title="What your role does" hint={v.me.staff !== null ? `${v.me.staff} Staff` : undefined} testId="seat-job">
      <p className={`me-job spec-${v.me.role}`}>{SEAT_JOB[v.me.role]}</p>
      {v.me.staff !== null && <p className="muted">{v.me.staff} Staff on your list this turn. Greens and Ambers need one each; Reds and ICU patients need two. Staff changes take effect next turn.</p>}
    </Fold>
    {v.me.objective && <section aria-label="Your secret objective"><Secret label="Show my secret objective" testId="secret-objective">
      <article className="objective"><span className="muted">Your secret objective. Keep it to yourself until the debrief.</span><h3>{v.me.objective.title}</h3><p>{v.me.objective.text}</p></article>
    </Secret></section>}
  </>;
}

/** Keep resource decisions revisable with the rest of the Lead's plan. */
function LeadTools({ v, act }: { v: SeatView; act: (a: Affordance) => Promise<boolean> }): ReactElement | null {
  if (!v.me.isLead) return null;
  const lead = v.seatActions.filter(a => a.group === 'lead' && a.cmd.type !== 'ALLOCATE' && a.cmd.type !== 'BOOK');
  const allocations = v.seatActions.filter(a => a.cmd.type === 'ALLOCATE');
  const slots = [{ key: 'scanner0', label: 'Scanner 1', current: v.board.scanner[0]?.allocatedTo }, { key: 'scanner1', label: 'Scanner 2', current: v.board.scanner[1]?.allocatedTo }, { key: 'theatre', label: 'Theatre', current: v.board.theatre.allocatedTo }];
  return <section className="lead-tools" aria-label="Lead tools">
    <h3 className="sec-title">You are the Lead</h3>
    <p className="muted">Keep equipment for one team, or leave it open. Scanners are open to everyone. If more scans are planned than there are scanners, the highest-priority scans go first.</p>
    <p className={v.coordination.timeOut ? 'phase-note' : 'muted'} role={v.coordination.timeOut ? 'alert' : undefined}>
      {v.coordination.timeOut && 'A Time-Out is planned for this turn. '}
      A Time-Out called by the Lead cancels this table’s care, discharges and referrals for the turn without using any actions, and patients’ waiting time does not move on. Bookings and expected arrivals still go ahead.
    </p>
    {slots.map(slot => {
      const planned = v.draft?.actions.findLast(a => a.cmd.type === 'ALLOCATE' && a.cmd.slot === slot.key)?.cmd;
      const current = planned?.type === 'ALLOCATE' ? planned.to : slot.current;
      return <label className="alloc-row" key={slot.key}><span className="alloc-label">{slot.label}</span>
        <select className="input alloc-select" value={current ?? ''} disabled={v.clock.phase !== 'ACT' || v.clock.paused} data-testid={`alloc-${slot.key}`} onChange={event => {
          const a = allocations.find(a => a.cmd.type === 'ALLOCATE' && a.cmd.slot === slot.key && a.cmd.to === (event.target.value || null));
          if (a?.enabled) void act(a);
        }}>
          <option value="">{slot.key === 'theatre' ? 'Shared in table order' : 'Open to everyone'}</option>{v.board.seats.map(s => <option value={s.role} key={s.role}>{ROLE_TITLE[s.role]}</option>)}
        </select>
      </label>;
    })}
    <div className="row wrap">{lead.map(a => <button key={a.id} className={`btn btn-small ${a.cmd.type === 'SET_DIVERT' ? 'btn-danger' : ''}`} disabled={!a.enabled} title={a.reason} onClick={() => act(a)} data-testid={`act-${a.id}`}>
      {a.label}{a.cost > 0 ? ` · ${a.cost} actions` : ''}
    </button>)}</div>
    {lead.some(a => !a.enabled && a.reason) && <ul className="why-list">{lead.filter(a => !a.enabled && a.reason).map(a => <li key={a.id}>{a.label}: {a.reason}</li>)}</ul>}
  </section>;
}

export function TeamTab({ v, act, respond, busy }: TeamControls & { act: (a: Affordance) => Promise<boolean> }) {
  const loans = v.seatActions.filter(a => a.cmd.type === 'LEND_STAFF');
  return <div className="team">
    <Referrals v={v} respond={respond} busy={busy} />
    <TeamDetails v={v} />
    <LeadTools v={v} act={act} />
    {v.me.isLead && <Plan v={v} act={act} />}
    {loans.length > 0 && <section className="lead-tools" aria-label="Staff loans"><h3 className="sec-title">Staff for next turn</h3><div className="row wrap">
      {loans.map(a => <button key={a.id} className="btn btn-small" disabled={!a.enabled || busy} title={a.reason} onClick={() => act(a)}>{a.label}</button>)}
    </div></section>}
  </div>;
}

/** Puts the Lead's resource requests beside the published forecast. */
function Plan({ v, act }: { v: SeatView; act: (a: Affordance) => Promise<boolean> }): ReactElement {
  const bookings = v.seatActions.filter((a) => a.cmd.type === 'BOOK');
  return (
    <section className="lead-tools" aria-label="Plan">
      <h3 className="sec-title">Plan the next three turns</h3>
      {(v.me.role === 'SURG' || v.me.role === 'ICU') && <p>{v.board.theatreDemand} patient{v.board.theatreDemand === 1 ? '' : 's'} on chosen routes still needing Theatre.</p>}
      <div className="kit-grid" style={{ gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' }}>
        {[1, 2, 3].map((offset) => {
          const turn = v.clock.turn + offset;
          const forecast = v.board.forecast.find((row) => row.turn === turn);
          return (
            <div className="kit" key={turn} data-testid={`plan-turn-${turn}`}>
              <strong>Turn {turn}</strong>
              {forecast && <span className="kit-detail">{forecast.hour} · {forecastText(forecast)}</span>}
              {turn >= v.clock.lastTurn ? <span className="kit-detail">Handover / after shift</span> : !forecast && <span className="kit-detail">No forecast published</span>}
              {v.board.bookings.filter((b) => b.from <= turn && (b.until === null || b.until >= turn)).map((b, i) => (
                <span className="tag" key={`${b.kind}-${b.bookedTurn}-${i}`}>{RESOURCE_TITLE[b.kind]} booked</span>
              ))}
            </div>
          );
        })}
      </div>
      <p className="muted">{v.board.callsLeft} calls for help left, shared by planned Locums and crisis calls.</p>
      <p className="muted">Bed meeting: the first discharge on each surgical, medical and Quiet Wing list is free in its booked turns. It adds no extra free discharges while Discharge Lounge Opens is active.</p>
      <div className="row wrap">
        {bookings.map((a) => (
          <button key={a.id} className="btn btn-small" disabled={!a.enabled} title={a.reason} onClick={() => act(a)} data-testid={`act-${a.id}`}>{a.label} · free</button>
        ))}
      </div>
      {bookings.some((a) => !a.enabled) && <ul className="why-list">{bookings.filter((a) => !a.enabled).map((a) => <li key={a.id}>{a.label}: {a.reason}</li>)}</ul>}
    </section>
  );
}
